import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAdminGetProduct, useAdminUpdateProduct, getAdminGetProductQueryKey } from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Pencil, Check, X } from 'lucide-react';
import { DefaultEntitlementForm } from '@/components/admin/DefaultEntitlementForm';
import { ProductTrialsForm } from '@/components/admin/ProductTrialsForm';
import { StripePricesForm } from '@/components/admin/StripePricesForm';
import { ConsultantHourPackagesForm } from '@/components/admin/ConsultantHourPackagesForm';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

/**
 * Admin product detail page for viewing and managing a specific product
 */
export default function AdminProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State for editing display name
  const [isEditingDisplayName, setIsEditingDisplayName] = useState(false);
  const [editedDisplayName, setEditedDisplayName] = useState('');

  // State for editing trial reminder days
  const [isEditingReminderDays, setIsEditingReminderDays] = useState(false);
  const [editedReminderDays, setEditedReminderDays] = useState('');

  // Fetch product details
  const {
    data: productData,
    isLoading: isLoadingProduct,
    error: productError,
  } = useAdminGetProduct(productId!, {
    query: {
      enabled: !!productId,
    },
  });

  const product = productData?.data;

  // Sync edited display name when product data changes
  useEffect(() => {
    if (product?.displayName) {
      setEditedDisplayName(product.displayName);
    }
  }, [product?.displayName]);

  // Sync edited reminder days when product data changes
  useEffect(() => {
    if (product?.trialReminderDays) {
      setEditedReminderDays(product.trialReminderDays.join(', '));
    } else {
      setEditedReminderDays('');
    }
  }, [product?.trialReminderDays]);

  // Update product mutation
  const { mutate: updateProduct, isPending: isUpdating } = useAdminUpdateProduct({
    mutation: {
      onSuccess: () => {
        toast.success('Product updated successfully');
        setIsEditingDisplayName(false);
        setIsEditingReminderDays(false);
        queryClient.invalidateQueries({
          queryKey: getAdminGetProductQueryKey(productId!),
        });
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'UpdateProduct',
          productId,
        });
        toast.error(
          'Failed to update product. ' +
            (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  /**
   * Handles saving the display name
   */
  const handleSaveDisplayName = () => {
    if (!editedDisplayName.trim()) {
      toast.error('Display name cannot be empty');
      return;
    }
    updateProduct({
      productId: productId!,
      data: { displayName: editedDisplayName.trim() },
    });
  };

  /**
   * Handles canceling the display name edit
   */
  const handleCancelDisplayNameEdit = () => {
    setEditedDisplayName(product?.displayName || '');
    setIsEditingDisplayName(false);
  };

  /**
   * Parses comma-separated string of reminder days into an array of numbers.
   * Returns null if empty (to use default), or an array of valid numbers.
   */
  const parseReminderDays = (input: string): number[] | null => {
    const trimmed = input.trim();
    if (!trimmed) {
      return null; // Use default [7, 1]
    }
    const parts = trimmed.split(',').map((s) => s.trim()).filter((s) => s);
    const numbers = parts.map((p) => parseInt(p, 10));
    if (numbers.some((n) => isNaN(n))) {
      throw new Error('All values must be valid numbers');
    }
    if (numbers.some((n) => n <= 0)) {
      throw new Error('All values must be positive');
    }
    if (numbers.some((n) => n > 90)) {
      throw new Error('Values cannot exceed 90');
    }
    if (new Set(numbers).size !== numbers.length) {
      throw new Error('Values must be unique');
    }
    if (numbers.length > 10) {
      throw new Error('Maximum 10 values allowed');
    }
    return numbers;
  };

  /**
   * Handles saving the trial reminder days
   */
  const handleSaveReminderDays = () => {
    try {
      const reminderDays = parseReminderDays(editedReminderDays);
      updateProduct({
        productId: productId!,
        data: { trialReminderDays: reminderDays ?? undefined },
      });
    } catch (error: any) {
      toast.error(error.message || 'Invalid reminder days format');
    }
  };

  /**
   * Handles canceling the reminder days edit
   */
  const handleCancelReminderDaysEdit = () => {
    if (product?.trialReminderDays) {
      setEditedReminderDays(product.trialReminderDays.join(', '));
    } else {
      setEditedReminderDays('');
    }
    setIsEditingReminderDays(false);
  };

  if (isLoadingProduct) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-[200px]" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (productError || !product) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard/admin/products')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>
        <div className="text-center text-destructive py-8">
          <p>Failed to load product: {(productError as any)?.message || 'Product not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard/admin/products')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold">Product: {product.productId}</h1>
        <p className="text-muted-foreground">
          Manage Stripe prices, default entitlements, and trials for this product
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Product Information</CardTitle>
            <CardDescription>Details about this product</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium">Product ID</p>
              <p className="text-lg font-mono">{product.productId}</p>
            </div>
            <div>
              <p className="text-sm font-medium">Display Name</p>
              {isEditingDisplayName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedDisplayName}
                    onChange={(e) => setEditedDisplayName(e.target.value)}
                    maxLength={100}
                    className="max-w-xs"
                    disabled={isUpdating}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSaveDisplayName}
                    disabled={isUpdating || !editedDisplayName.trim()}
                  >
                    <Check className="h-4 w-4 text-green-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelDisplayNameEdit}
                    disabled={isUpdating}
                  >
                    <X className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-lg">{product.displayName}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingDisplayName(true)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-medium">Default Entitlement Status</p>
              {product.hasDefaultEntitlement ? (
                <Badge variant="default">Configured</Badge>
              ) : (
                <Badge variant="outline">Not Set</Badge>
              )}
            </div>
            <div>
              <p className="text-sm font-medium">Trials Count</p>
              <p className="text-lg">{product.trialsCount || 0}</p>
            </div>
            <div>
              <p className="text-sm font-medium">Trial Reminder Days</p>
              {isEditingReminderDays ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedReminderDays}
                    onChange={(e) => setEditedReminderDays(e.target.value)}
                    placeholder="7, 1"
                    className="max-w-xs"
                    disabled={isUpdating}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSaveReminderDays}
                    disabled={isUpdating}
                  >
                    <Check className="h-4 w-4 text-green-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelReminderDaysEdit}
                    disabled={isUpdating}
                  >
                    <X className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-lg">
                    {product.trialReminderDays
                      ? product.trialReminderDays.join(', ')
                      : 'Default (7, 1)'}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingReminderDays(true)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Days before trial expiration to send reminders. Leave empty for default (7, 1).
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Stats</CardTitle>
            <CardDescription>Summary of product activity</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium">Total Trials</p>
              <p className="text-2xl font-bold">{product.trialsCount || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        <StripePricesForm productId={productId!} />
        <ConsultantHourPackagesForm productId={productId!} />
        <DefaultEntitlementForm productId={productId!} />
        <ProductTrialsForm productId={productId!} />
      </div>
    </div>
  );
}
